import React, { useState } from "react";
import { Dropdown, DropdownButton } from "react-bootstrap";
import { formatDate } from "../../utils/helpers";
import { LEAD, PROSPECT, HOMEOWNER, REALTOR, MARKETING_ONLY } from "../../utils/constants";
import PastActivityAppointmentListView from "./past-activities-appointment";
import PastActivityFollowupListView from "./past-activities-followup";
import PastActivityPhoneCallsListView from "./past-activities-phonecall";
import PastActivityEmailsListView from "./past-activities-emails";
import PastActivityEbombsListView from "./past-activities-ebombs";
import PastActivityMassEmailsListView from "./past-activities-mass-emails";
import PastActivityPhoneSmsListView from "./past-activities-sms";
import PastActivityWalkInListView from "./past-activities-walk-in";
import PastActivityTrafficListView from "./past-activities-traffic";
import PastActivityFloorPlanListView from "./past-activities-floor-plan";
import AccordionView from "../../components/accordion/accordion";
import PastActivityWebformListView from "./past-activities-webforms";
import "./style.css";

const floorPlanFilterItems = {
  SaveFloorPlanActivity: "Saved Floor Plans",
  FavoriteFloorPlanActivity: "Favorite Floor Plans",
};

/**
 * @module PastActivitiesViewModule
 */

/**
 * this component is used to render the past activities
 * @param {object} pastActivitiesData contain all information regarding the activities
 * @param {string} type type of the details page
 * @param {function} onSubmit
 * @param {number} id
 */
const PastActivitiesView = ({ pastActivitiesData, id, onSubmit, type, readOnly }) => {
  const [title, setTitle] = useState("All");
  const [value, setValue] = useState("All");

  const dropdownItems = {
    All: "All",
    ...(type !== "webform" && type !== REALTOR
      ? {
          "Mass Email": "Mass Emails",
        }
      : {}),
    ...(type === PROSPECT
      ? {
          Appointment: "Appointments",
          Prospect_Follow_Up: "Follow-ups",
          Prospect_Walk_In: "Walk-Ins",
          Traffic: "Traffic",
          ...floorPlanFilterItems,
          MailchimpEmailType: "Ebombs",
        }
      : {}),
    ...(type === HOMEOWNER
      ? {
          Appointment: "Appointments",
          Homeowner_Follow_Up: "Follow-ups",
          Traffic: "Traffic",
          ...floorPlanFilterItems,
          MailchimpEmailType: "Ebombs",
        }
      : {}),
    ...(type === LEAD
      ? {
          Appointment: "Appointments",
          Lead_Follow_Up: "Follow-ups",
          Traffic: "Traffic",
          ...floorPlanFilterItems,
          MailchimpEmailType: "Ebombs",
        }
      : {}),
    ...(type === MARKETING_ONLY
      ? {
          Appointment: "Appointments",
          Marketing_Only_Follow_Up: "Follow-ups",
          Prospect_Walk_In: "Walk-Ins",
          Traffic: "Traffic",
          ...floorPlanFilterItems,
          MailchimpEmailType: "Ebombs",
        }
      : {}),
    ...(type === REALTOR
      ? {
          Realtor_Appointment: "Appointments",
          Realtor_Follow_Up: "Follow-ups",
          MailchimpEmailType: "Ebombs",
        }
      : {
          CustomerFormSubmission: "Web Forms",
          PhoneCall: "Phone Calls",
          Text: "Texts",
          Email: "Emails",
          ...(type === "webform" ? floorPlanFilterItems : {}),
        }),
  };

  /**
   * this function counts all types of activities separately
   * @function
   * @inner
   * @memberof module:PastActivitiesViewModule
   * @param {string} date the selected date
   * @returns {string}
   */
  let countActivities = (date) => {
    const counts =
      pastActivitiesData &&
      pastActivitiesData.reduce((acc, cur) => {
        const isAppointment = cur.__typename
          .toLowerCase()
          .includes("appointment");
        const isFollowUp = cur.__typename.toLowerCase().includes("follow_up");

        if ((isAppointment || isFollowUp) && !cur.done) {
          return acc;
        }

        acc[cur.__typename] =
          formatDate(new Date(cur.start)) === date
            ? (acc[cur.__typename] || 0) + 1
            : acc[cur.__typename] || 0;

        return acc;
      }, {});

    switch (title) {
      case "All":
        return (
          counts &&
          (counts.PhoneCall ? counts.PhoneCall + " Phone Call(s), " : "") +
            (counts.Text ? counts.Text + " Text(s), " : "") +
            (counts.Email ? counts.Email + " Email(s), " : "") +
            (counts["Mass Email"]
              ? counts["Mass Email"] + " Mass Email(s), "
              : "") +
            (counts.MailchimpEmailType
              ? counts.MailchimpEmailType + " Ebomb(s), "
              : "") +
            (counts.Appointment
              ? counts.Appointment + " Appointment(s), "
              : "") +
            (counts.Lead_Follow_Up
              ? counts.Lead_Follow_Up + " Follow-up(s), "
              : "") +
            (counts.Prospect_Follow_Up
              ? counts.Prospect_Follow_Up + " Follow-up(s), "
              : "") +
            (counts.Prospect_Walk_In
              ? counts.Prospect_Walk_In + " Walk-In(s), "
              : "") +
            (counts.Homeowner_Follow_Up
              ? counts.Homeowner_Follow_Up + " Follow-up(s), "
              : "") +
            (counts.Marketing_Only_Follow_Up
              ? counts.Marketing_Only_Follow_Up + " Follow-up(s), "
              : "") +
            (counts.Realtor_Follow_Up
              ? counts.Realtor_Follow_Up + " Follow-up(s), "
              : "") +
            (counts.Traffic
              ? counts.Traffic + " Traffic(s), "
              : "") +
            (counts.SaveFloorPlanActivity
              ? counts.SaveFloorPlanActivity + " Saved Floor Plan(s), "
              : "") +
            (counts.FavoriteFloorPlanActivity
              ? counts.FavoriteFloorPlanActivity + " Favorite Floor Plan(s), "
              : "") +
            (counts.CustomerFormSubmission
              ? counts.CustomerFormSubmission + " Web Form(s) "
              : "")
        );
      case "Emails":
        return counts.Email ? counts.Email + " Email(s) " : "";
      case "Mass Emails":
        return counts["Mass Email"]
          ? counts["Mass Email"] + " Mass Email(s) "
          : "";
      case "Ebombs":
        return counts.MailchimpEmailType
          ? counts.MailchimpEmailType + " Ebomb(s) "
          : "";
      case "Phone Calls":
        return counts.PhoneCall ? counts.PhoneCall + " Phone Call(s) " : "";
      case "Texts":
        return counts.Text ? counts.Text + " Texts " : "";
      case "Appointments":
        return counts.Appointment
          ? counts.Appointment + " Appointment(s) "
          : "";
      case "Follow-ups":
        const count =
          counts.Lead_Follow_Up ||
          counts.Prospect_Follow_Up ||
          counts.Homeowner_Follow_Up ||
          counts.Marketing_Only_Follow_Up ||
          counts.Realtor_Follow_Up ||
          0;

        return count ? count + " Follow-up(s) " : "";
      case "Walk-Ins":
        return counts.Prospect_Walk_In
          ? counts.Prospect_Walk_In + " Walk-In(s), "
          : "";
      case "Web Forms":
        return counts.CustomerFormSubmission
          ? counts.CustomerFormSubmission + " Web Form(s) "
          : "";
      case "Traffic":
        return counts.Traffic
          ? counts.Traffic + " Traffic(s), "
          : "";
      case "Saved Floor Plans":
        return counts.SaveFloorPlanActivity
          ? counts.SaveFloorPlanActivity + " Saved Floor Plan(s), "
          : "";
      case "Favorite Floor Plans":
        return counts.FavoriteFloorPlanActivity
          ? counts.FavoriteFloorPlanActivity + " Favorite Floor Plan(s), "
          : "";
      default:
        return;
    }
  };

  /**
   * this function renders the activities
   * @function
   * @inner
   * @memberof module:PastActivitiesViewModule
   * @param {object} item contains all information related to the activities
   * @param {string} __typename type of the activity
   * @returns {JSX.Element}
   */
  const renderPastActivities = (item) => {
    const isAppointment = item?.__typename
      ?.toLowerCase()
      ?.includes("appointment");
    const isFollowUp = item?.__typename?.toLowerCase()?.includes("follow_up");

    return (
      <React.Fragment>
        {item.__typename === "PhoneCall" && (
          <AccordionView
            component={PastActivityPhoneCallsListView}
            pastActivitiesData={item}
            onSubmit={onSubmit}
            type={type}
            toggleAction={true}
            style={{
              borderLeft: "3px solid #50E3C2",
              backgroundColor: "rgba(80, 227, 197, 0.3)",
              color: "white",
            }}
            readOnly={readOnly}
          />
        )}
        {item.__typename === "Text" && (
          <AccordionView
            component={PastActivityPhoneSmsListView}
            pastActivitiesData={item}
            onSubmit={onSubmit}
            type={type}
            toggleAction={true}
            style={{
              borderLeft: "3px solid #C48E48",
              backgroundColor: "rgb(87, 71, 49,0.3)",
              color: "white",
            }}
            readOnly={readOnly}
          />
        )}
        {item.__typename === "Email" && (
          <AccordionView
            component={PastActivityEmailsListView}
            pastActivitiesData={item}
            toggleAction={true}
            style={{
              borderLeft: "3px solid #FF7A00",
              backgroundColor: "rgba(255, 122, 0, 0.3)",
              color: "white",
            }}
          />
        )}
        {item.__typename === "MailchimpEmailType" && (
          <AccordionView
            component={PastActivityEbombsListView}
            pastActivitiesData={item}
            style={{
              borderLeft: "3px solid #D0021B",
              backgroundColor: "rgba(208, 2, 27, 0.3)",
              color: "white",
            }}
          />
        )}
        {item.__typename === "Mass Email" && (
          <AccordionView
            component={PastActivityMassEmailsListView}
            pastActivitiesData={item}
            toggleAction={true}
            style={{
              borderLeft: "3px solid #9400ff",
              backgroundColor: "rgba(148, 0, 255, 0.3)",
              color: "white",
            }}
          />
        )}
        {isAppointment && item.done && (
          <AccordionView
            component={PastActivityAppointmentListView}
            pastActivitiesData={item}
            style={{
              borderLeft: `3px solid ${
                type === REALTOR ? "#d4ba2a" : "#92af00"
              }`,
              backgroundColor:
                type === REALTOR
                  ? "rgba(212, 186, 42, 0.5)"
                  : "rgba(183, 214, 135, 0.3)",
              color: "white",
            }}
          />
        )}
        {isFollowUp && item.done && (
          <AccordionView
            component={PastActivityFollowupListView}
            pastActivitiesData={item}
            style={{
              borderLeft: `3px solid ${
                item.__typename === "Realtor_Follow_Up" ? "#a84264" : "#4c7ef9"
              }`,
              backgroundColor:
                item.__typename === "Realtor_Follow_Up"
                  ? "rgba(212, 42, 150, 0.3)"
                  : "rgba(76, 126, 249, 0.3)",
              color: "white",
            }}
          />
        )}
        {item.__typename === "Prospect_Walk_In" && (
          <AccordionView
            component={PastActivityWalkInListView}
            pastActivitiesData={item}
            style={{
              borderLeft: "3px solid #A84264",
              backgroundColor: "rgba(168, 66, 100, 0.3)",
              color: "white",
            }}
          />
        )}
        {item.__typename === "CustomerFormSubmission" && (
          <AccordionView
            component={PastActivityWebformListView}
            pastActivitiesData={item}
            style={{
              borderLeft: "3px solid #FF5E5E",
              backgroundColor: "rgba(255, 94, 94, 0.3)",
              color: "white",
            }}
          />
        )}
        {item.__typename === "Traffic" && (
          <AccordionView
            component={PastActivityTrafficListView}
            pastActivitiesData={item}
            onSubmit={onSubmit}
            type={type}
            style={{
              borderLeft: "3px solid #00ff40",
              backgroundColor: "rgba(3, 255, 66, 0.3)",
              color: "white",
            }}
            readOnly={readOnly}
          />
        )}
        {(item.__typename === "SaveFloorPlanActivity" ||
          item.__typename === "FavoriteFloorPlanActivity") && (
          <AccordionView
            component={PastActivityFloorPlanListView}
            pastActivitiesData={item}
            style={{
              borderLeft: `3px solid ${
                item.__typename === "FavoriteFloorPlanActivity" ? "#880E4F" : "#1B5E20"
              }`,
              backgroundColor:
                item.__typename === "FavoriteFloorPlanActivity"
                  ? "rgba(136, 14, 79, 0.3)"
                  : "rgba(27, 94, 32, 0.3)",
              color: "white",
            }}
          />
        )}
      </React.Fragment>
    );
  };

  let prevDate = null;

  return (
    <div>
      <div className="d-flex justify-content-between past-activities-header">
        <h3 className="ml-0">Past Activities</h3>
        <div className="d-flex justify-content-end">
          <div className="past-activities-filters d-flex">
            <DropdownButton
              alignRight
              drop="down"
              variant="secondary"
              id="past-activities-item-btn"
              key="2"
              title={title}
              className="past-activity-dropdown"
            >
              {Object.keys(dropdownItems).map((key, index) => {
                return (
                  <Dropdown.Item
                    onSelect={() => {
                      setTitle(dropdownItems[key]);
                      setValue(key);
                    }}
                    key={index}
                  >
                    {dropdownItems[key]}
                  </Dropdown.Item>
                );
              })}
            </DropdownButton>
          </div>
        </div>
      </div>
      <div className="past-activities-wrapper">
        {pastActivitiesData &&
          pastActivitiesData.map((item, index) => {
            if (value === "All" || item.__typename === value) {
              let headerComponent = null;
              let itemDate = formatDate(item.start);

              if (itemDate !== prevDate) {
                prevDate = itemDate;
                const count = countActivities(itemDate).replace(/,\s$/, "");
                headerComponent = count && (
                  <div key={index} className="mb-2">
                    <div className="d-flex justify-content-between small text-muted mb-2 mr-1">
                      <span>{itemDate}</span>
                      <span>{count}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={index}>
                  {headerComponent}
                  {renderPastActivities(item)}
                </div>
              );
            }
            return null;
          })}
      </div>
    </div>
  );
};

export default PastActivitiesView;
